import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { showBanner } from '../../global-state/banner-slice'
import { opportunityServices, transactionServices, investmentServices } from '../../services'
import type {
  InterestPayoutLine,
  InterestPayoutPreviewResponse,
  InterestPayoutRequest,
  InterestPayoutResponse,
} from '../../services/transaction-service'
import { InterestPayoutFormPresenter } from './interest-payout-form-presenter'
import type {
  ConfirmPayoutLineRow,
  EligibleInvestmentOption,
  OpportunityOption,
} from './interest-payout-form-types'

export type { EligibleInvestmentOption, OpportunityOption } from './interest-payout-form-types'

type OpportunitiesApiResponse = {
  opportunities?: Array<{
    id: number
    opportunity_title?: string
    metadata?: { is_fixed_income?: boolean }
  }>
}

const isActionableLine = (status: InterestPayoutLine['status']) =>
  status === 'processed' || status === 'reinvested'

const InterestPayoutFormContainer = () => {
  const dispatch = useDispatch()
  const [opportunities, setOpportunities] = useState<OpportunityOption[]>([])
  const [opportunitiesLoading, setOpportunitiesLoading] = useState(true)
  const [selectedOpportunityIds, setSelectedOpportunityIds] = useState<Set<number>>(new Set())
  const [eligibleInvestments, setEligibleInvestments] = useState<EligibleInvestmentOption[]>([])
  const [selectedInvestmentIds, setSelectedInvestmentIds] = useState<Set<number>>(new Set())
  const [investmentsLoading, setInvestmentsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [result, setResult] = useState<InterestPayoutResponse | null>(null)
  const [resultError, setResultError] = useState<string | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [previewData, setPreviewData] = useState<InterestPayoutPreviewResponse | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [reinvestSelection, setReinvestSelection] = useState<Set<number>>(new Set())
  const lastPayloadRef = useRef<InterestPayoutRequest | null>(null)

  const listableOpportunityIds = useMemo(
    () => opportunities.filter((o) => o.opportunity_title).map((o) => o.id),
    [opportunities],
  )

  const loadOpportunities = useCallback(async () => {
    setOpportunitiesLoading(true)
    try {
      const response = (await opportunityServices.getAllOpportunities()) as OpportunitiesApiResponse
      if (response?.opportunities && Array.isArray(response.opportunities)) {
        const fixedIncomeOnly = response.opportunities.filter(
          (o) => o.metadata?.is_fixed_income === true,
        )
        setOpportunities(
          fixedIncomeOnly.map((o) => ({
            id: o.id,
            opportunity_title: o.opportunity_title ?? `Opportunity ${o.id}`,
          })),
        )
      } else {
        setOpportunities([])
      }
    } catch {
      dispatch(
        showBanner({
          text: 'Failed to load opportunities.',
          variant: 'error-banner',
        }),
      )
      setOpportunities([])
    } finally {
      setOpportunitiesLoading(false)
    }
  }, [dispatch])

  useEffect(() => {
    loadOpportunities()
  }, [loadOpportunities])

  useEffect(() => {
    if (selectedOpportunityIds.size === 0) {
      setEligibleInvestments([])
      setSelectedInvestmentIds(new Set())
      return
    }

    let cancelled = false
    const loadInvestments = async () => {
      setInvestmentsLoading(true)
      try {
        const lists = await Promise.all(
          Array.from(selectedOpportunityIds).map(async (oppId) => {
            const res = await investmentServices.getInvestments({
              opportunity_id: String(oppId),
              limit: '100',
              offset: '0',
            })
            if (res?.status !== 200 || !res.investments) return []
            const opp = opportunities.find((o) => o.id === oppId)
            const title = opp?.opportunity_title ?? `Opportunity ${oppId}`
            return (res.investments as Record<string, unknown>[])
              .filter(
                (inv) => inv.status === 'TRADE_EXECUTED' && (inv.unit_allocation_date ?? '') !== '',
              )
              .map((inv: Record<string, unknown>) => ({
                id: inv.id as number,
                investor_id: Number(inv.investor_id ?? 0),
                opportunity_id: oppId,
                opportunity_title: title,
                label: `${(inv.name as string) || (inv.email as string) || 'Client'} · #${
                  inv.id as number
                }`,
              }))
          }),
        )
        if (!cancelled) {
          setEligibleInvestments(lists.flat())
        }
      } catch {
        if (!cancelled) {
          setEligibleInvestments([])
          dispatch(
            showBanner({
              text: 'Failed to load investments for selected opportunities.',
              variant: 'error-banner',
            }),
          )
        }
      } finally {
        if (!cancelled) {
          setInvestmentsLoading(false)
        }
      }
    }

    loadInvestments()
    return () => {
      cancelled = true
    }
  }, [selectedOpportunityIds, opportunities, dispatch])

  useEffect(() => {
    setSelectedInvestmentIds((prev) => {
      const next = new Set<number>()
      const eligible = new Set(eligibleInvestments.map((e) => e.id))
      for (const id of prev) {
        if (eligible.has(id)) next.add(id)
      }
      return next
    })
  }, [eligibleInvestments])

  const toggleOpportunity = useCallback((id: number) => {
    setSelectedOpportunityIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const selectAllOpportunities = useCallback(() => {
    setSelectedOpportunityIds((prev) => {
      if (prev.size === listableOpportunityIds.length) {
        return new Set()
      }
      return new Set(listableOpportunityIds)
    })
  }, [listableOpportunityIds])

  const toggleInvestment = useCallback((id: number) => {
    setSelectedInvestmentIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const selectAllInvestments = useCallback(() => {
    setSelectedInvestmentIds((prev) => {
      const allIds = eligibleInvestments.map((e) => e.id)
      if (prev.size === allIds.length) return new Set()
      return new Set(allIds)
    })
  }, [eligibleInvestments])

  const selectionStats = useMemo(() => {
    const list =
      selectedInvestmentIds.size > 0
        ? eligibleInvestments.filter((e) => selectedInvestmentIds.has(e.id))
        : eligibleInvestments
    return {
      investmentCount: list.length,
      investorCount: new Set(list.map((e) => e.investor_id)).size,
    }
  }, [eligibleInvestments, selectedInvestmentIds])

  const confirmRows = useMemo((): ConfirmPayoutLineRow[] => {
    if (!previewData) return []
    const byId = new Map(eligibleInvestments.map((e) => [e.id, e]))
    const rows: ConfirmPayoutLineRow[] = []
    for (const resultItem of previewData.results) {
      for (const line of resultItem.payout_lines ?? []) {
        const match = byId.get(line.investment_id)
        rows.push({
          investment_id: line.investment_id,
          investor_id: line.investor_id,
          opportunity_id: resultItem.opportunity_id,
          opportunity_title: resultItem.opportunity_title,
          label: match?.label ?? `Investor #${line.investor_id} · #${line.investment_id}`,
          status: line.status,
          interest_amount_cents: line.interest_amount ?? 0,
          interest_rate: line.interest_rate,
          actionable: isActionableLine(line.status),
        })
      }
    }
    return rows
  }, [previewData, eligibleInvestments])

  const actionableConfirmRows = useMemo(
    () => confirmRows.filter((row) => row.actionable),
    [confirmRows],
  )

  const buildPayload = useCallback(
    (dates: {
      payout_date?: string
      period_start_date: string
      period_end_date: string
    }): InterestPayoutRequest => ({
      opportunity_ids: Array.from(selectedOpportunityIds),
      investment_ids:
        selectedInvestmentIds.size > 0 ? Array.from(selectedInvestmentIds) : undefined,
      ...dates,
    }),
    [selectedOpportunityIds, selectedInvestmentIds],
  )

  const handleSubmit = useCallback(
    async (payload: InterestPayoutRequest) => {
      setResult(null)
      setResultError(null)
      setIsSubmitting(true)
      try {
        const response = await transactionServices.createInterestPayouts(payload)
        if (response.status === 200 && response.data) {
          setResult(response.data)
          dispatch(
            showBanner({
              text: 'Interest payout processed successfully.',
              variant: 'success-banner',
            }),
          )
        } else {
          const errorMessage = response.error || 'Failed to process interest payout.'
          setResultError(errorMessage)
          dispatch(
            showBanner({
              text: errorMessage,
              variant: 'error-banner',
            }),
          )
        }
      } catch {
        const errorMessage = 'Failed to process interest payout. Please try again.'
        setResultError(errorMessage)
        dispatch(
          showBanner({
            text: errorMessage,
            variant: 'error-banner',
          }),
        )
      } finally {
        setIsSubmitting(false)
      }
    },
    [dispatch],
  )

  const handleReview = useCallback(
    async (dates: { payout_date?: string; period_start_date: string; period_end_date: string }) => {
      setPreviewError(null)
      setPreviewData(null)
      setReinvestSelection(new Set())
      const payload = buildPayload(dates)
      lastPayloadRef.current = payload
      setPreviewLoading(true)
      try {
        const res = await transactionServices.previewInterestPayouts(payload)
        if (res.status === 200 && res.data) {
          const initialReinvest = new Set<number>()
          for (const resultItem of res.data.results) {
            for (const line of resultItem.payout_lines ?? []) {
              if (line.status === 'reinvested') {
                initialReinvest.add(line.investment_id)
              }
            }
          }
          setReinvestSelection(initialReinvest)
          setPreviewData(res.data)
          setConfirmOpen(true)
        } else {
          const msg = res.error || 'Failed to load payout preview.'
          setPreviewError(msg)
          dispatch(
            showBanner({
              text: msg,
              variant: 'error-banner',
            }),
          )
        }
      } catch {
        const msg = 'Failed to load payout preview. Please try again.'
        setPreviewError(msg)
        dispatch(
          showBanner({
            text: msg,
            variant: 'error-banner',
          }),
        )
      } finally {
        setPreviewLoading(false)
      }
    },
    [buildPayload, dispatch],
  )

  const handleCloseConfirm = useCallback(() => {
    setConfirmOpen(false)
    setPreviewData(null)
    setReinvestSelection(new Set())
    lastPayloadRef.current = null
  }, [])

  const toggleReinvest = useCallback((investmentId: number) => {
    setReinvestSelection((prev) => {
      const next = new Set(prev)
      if (next.has(investmentId)) next.delete(investmentId)
      else next.add(investmentId)
      return next
    })
  }, [])

  const toggleReinvestAll = useCallback(() => {
    setReinvestSelection((prev) => {
      const allIds = actionableConfirmRows.map((row) => row.investment_id)
      if (allIds.length === 0) return prev
      if (prev.size === allIds.length && allIds.every((id) => prev.has(id))) {
        return new Set()
      }
      return new Set(allIds)
    })
  }, [actionableConfirmRows])

  const handleConfirmExecute = useCallback(async () => {
    const payload = lastPayloadRef.current
    if (!payload) return
    const reinvest_overrides = actionableConfirmRows.map((row) => ({
      investment_id: row.investment_id,
      reinvest: reinvestSelection.has(row.investment_id),
    }))
    setConfirmOpen(false)
    setPreviewData(null)
    setReinvestSelection(new Set())
    lastPayloadRef.current = null
    await handleSubmit({ ...payload, reinvest_overrides })
  }, [handleSubmit, actionableConfirmRows, reinvestSelection])

  return (
    <InterestPayoutFormPresenter
      opportunities={opportunities}
      opportunitiesLoading={opportunitiesLoading}
      selectedOpportunityIds={selectedOpportunityIds}
      onToggleOpportunity={toggleOpportunity}
      onSelectAllOpportunities={selectAllOpportunities}
      eligibleInvestments={eligibleInvestments}
      investmentsLoading={investmentsLoading}
      selectedInvestmentIds={selectedInvestmentIds}
      onToggleInvestment={toggleInvestment}
      onSelectAllInvestments={selectAllInvestments}
      selectionStats={selectionStats}
      onReview={handleReview}
      confirmOpen={confirmOpen}
      previewData={previewData}
      previewLoading={previewLoading}
      previewError={previewError}
      confirmRows={confirmRows}
      reinvestSelection={reinvestSelection}
      onToggleReinvest={toggleReinvest}
      onToggleReinvestAll={toggleReinvestAll}
      onCloseConfirm={handleCloseConfirm}
      onConfirmExecute={handleConfirmExecute}
      isSubmitting={isSubmitting}
      result={result}
      resultError={resultError}
    />
  )
}

export { InterestPayoutFormContainer }
